/**
 * class-date-picker.js
 * Custom element that renders a mini month calendar for class date selection.
 * Reads variant data from inline JSON and integrates with Dawn's product form.
 */

if (!customElements.get('class-date-picker')) {
  class ClassDatePicker extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.location = this.dataset.location;
      this.productTitle = this.dataset.productTitle || '';
      this.variants = [];
      this.variantsByDate = {};
      this.selectedDate = null;
      this.currentMonth = null;
      this.currentYear = null;
      this.quantity = 1;
      this.occasion = '';
      this.occasionText = '';
    }

    connectedCallback() {
      this.loadVariantData();

      // Class forms fail closed. A validated future date selection is the only
      // path that can re-enable the variant input and submit button.
      this.hideDefaultVariantPicker();
      this.hideDefaultQuantity();
      this.setSubmitState(false, this.variants.length === 0 ? 'No Upcoming Dates' : 'Select a Date');

      var productForm = this.closest('form') || document.querySelector('[data-type="add-to-cart-form"]');
      if (productForm) {
        var initialVariantInput = productForm.querySelector('input[name="id"]');
        if (initialVariantInput) initialVariantInput.disabled = true;

        var self = this;
        productForm.addEventListener('submit', function (event) {
          var selectedVariant = self.variantsByDate[self.selectedDate];
          var selectedDate = self.parseDate(self.selectedDate);
          var today = new Date();
          today.setHours(0, 0, 0, 0);

          if (!selectedVariant || !selectedVariant.available || !selectedDate || selectedDate < today) {
            event.preventDefault();
            event.stopImmediatePropagation();

            var variantInput = productForm.querySelector('input[name="id"]');
            if (variantInput) variantInput.disabled = true;

            var label = selectedVariant && !selectedVariant.available
              ? 'Sold Out'
              : (self.variants.length === 0 ? 'No Upcoming Dates' : 'Select a Date');
            self.setSubmitState(false, label);
          }
        }, true);
      }

      if (this.variants.length === 0) {
        var emptyCalendar = document.getElementById('class-date-calendar-' + this.sectionId);
        if (emptyCalendar) {
          emptyCalendar.innerHTML = '<p class="class-date-picker__empty">No upcoming dates are currently available.</p>';
        }
        return;
      }

      // Determine initial month: first future available date, or first date overall
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var firstDate = null;

      for (var i = 0; i < this.variants.length; i++) {
        var d = this.parseDate(this.variants[i].date);
        if (d && d >= today && this.variants[i].available) {
          firstDate = d;
          break;
        }
      }

      if (!firstDate && this.variants.length > 0) {
        firstDate = this.parseDate(this.variants[0].date);
      }

      if (firstDate) {
        this.currentMonth = firstDate.getMonth();
        this.currentYear = firstDate.getFullYear();
      } else {
        this.currentMonth = today.getMonth();
        this.currentYear = today.getFullYear();
      }

      // Check if URL has a pre-selected variant (from calendar click or direct link)
      var urlParams = new URLSearchParams(window.location.search);
      var variantParam = urlParams.get('variant');
      var selectParam = urlParams.get('select');

      if (variantParam) {
        // Find the date matching this variant ID
        for (var i = 0; i < this.variants.length; i++) {
          if (String(this.variants[i].id) === variantParam) {
            this.selectedDate = this.variants[i].date;
            var d = this.parseDate(this.variants[i].date);
            if (d) {
              this.currentMonth = d.getMonth();
              this.currentYear = d.getFullYear();
            }
            break;
          }
        }
      } else if (selectParam) {
        var preselectedDate = selectParam.split('T')[0];
        if (this.variantsByDate[preselectedDate]) {
          this.selectedDate = preselectedDate;
          var d = this.parseDate(preselectedDate);
          if (d) {
            this.currentMonth = d.getMonth();
            this.currentYear = d.getFullYear();
          }
        }
      }

      this.render();

      if (this.selectedDate) {
        this.selectDate(this.selectedDate);
      }
    }

    loadVariantData() {
      var dataEl = document.getElementById('class-variant-data-' + this.sectionId);
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('Failed to parse variant data:', e);
        this.variants = [];
      }

      // Keep only valid dates that can still be booked or joined by waitlist.
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var futureVariants = [];

      for (var i = 0; i < this.variants.length; i++) {
        var variantDate = this.parseDate(this.variants[i].date);
        if (variantDate && variantDate >= today) {
          futureVariants.push(this.variants[i]);
        }
      }

      this.variants = futureVariants;

      // Build lookup by date.
      this.variantsByDate = {};
      for (var i = 0; i < this.variants.length; i++) {
        var v = this.variants[i];
        this.variantsByDate[v.date] = v;
      }
    }

    parseDate(dateStr) {
      if (!dateStr) return null;
      var match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!match) return null;

      var year = parseInt(match[1], 10);
      var month = parseInt(match[2], 10);
      var day = parseInt(match[3], 10);
      var parsed = new Date(year, month - 1, day);

      if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
        return null;
      }

      return parsed;
    }

    setSubmitState(enabled, label) {
      var submitBtn = document.getElementById('ProductSubmitButton-' + this.sectionId);
      if (!submitBtn) return;

      submitBtn.disabled = !enabled;
      var labelEl = submitBtn.querySelector('span');
      if (labelEl) labelEl.textContent = label;
    }

    formatDateForProperty(dateStr) {
      // Convert YYYY-MM-DD to MM/DD/YYYY for the Date line item property
      var parts = dateStr.split('-');
      if (parts.length !== 3) return dateStr;
      return parts[1] + '/' + parts[2] + '/' + parts[0];
    }

    formatDateDisplay(dateStr) {
      var d = this.parseDate(dateStr);
      if (!d) return dateStr;
      var months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      return months[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
    }

    formatTime(timeStr) {
      // Time values are already display-formatted text (e.g. "9:00", "4:30pm")
      return timeStr || '';
    }

    formatPrice(cents) {
      var dollars = (cents / 100).toFixed(2);
      return '$' + dollars.replace(/\.00$/, '');
    }

    render() {
      var calendarEl = document.getElementById('class-date-calendar-' + this.sectionId);
      if (!calendarEl) return;

      var html = '';

      // Month header with navigation
      var months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

      html += '<div class="cdp-header">';
      html += '<button type="button" class="cdp-nav cdp-prev" aria-label="Previous month">&lsaquo;</button>';
      html += '<span class="cdp-month-year">' + months[this.currentMonth] + ' ' + this.currentYear + '</span>';
      html += '<button type="button" class="cdp-nav cdp-next" aria-label="Next month">&rsaquo;</button>';
      html += '</div>';

      // Day headers
      var dayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
      html += '<div class="cdp-grid cdp-day-headers">';
      for (var i = 0; i < 7; i++) {
        html += '<div class="cdp-day-header">' + dayHeaders[i] + '</div>';
      }
      html += '</div>';

      // Calendar days
      var firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
      var daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
      var today = new Date();
      today.setHours(0, 0, 0, 0);

      html += '<div class="cdp-grid cdp-days">';

      // Empty cells before first day
      for (var i = 0; i < firstDay; i++) {
        html += '<div class="cdp-day cdp-day--empty"></div>';
      }

      // Day cells
      for (var day = 1; day <= daysInMonth; day++) {
        var dateStr = this.currentYear + '-' +
          String(this.currentMonth + 1).padStart(2, '0') + '-' +
          String(day).padStart(2, '0');

        var variant = this.variantsByDate[dateStr];
        var cellDate = new Date(this.currentYear, this.currentMonth, day);
        var isPast = cellDate < today;

        var classes = ['cdp-day'];

        if (variant) {
          if (isPast) {
            classes.push('cdp-day--past');
          } else if (!variant.available) {
            classes.push('cdp-day--soldout');
          } else {
            classes.push('cdp-day--available');
          }
        }

        if (dateStr === this.selectedDate) {
          classes.push('cdp-day--selected');
        }

        var isClickable = variant && !isPast;

        if (isClickable) {
          html += '<button type="button" class="' + classes.join(' ') + '" data-date="' + dateStr + '">';
          html += day;
          html += '</button>';
        } else {
          html += '<div class="' + classes.join(' ') + '">' + day + '</div>';
        }
      }

      html += '</div>';

      calendarEl.innerHTML = html;

      // Bind events
      var self = this;

      var prevBtn = calendarEl.querySelector('.cdp-prev');
      var nextBtn = calendarEl.querySelector('.cdp-next');
      if (prevBtn) {
        var now = new Date();
        if (self.currentYear === now.getFullYear() && self.currentMonth === now.getMonth()) {
          prevBtn.disabled = true;
          prevBtn.classList.add('cdp-nav--disabled');
        }
        prevBtn.addEventListener('click', function () {
          var now = new Date();
          if (self.currentYear === now.getFullYear() && self.currentMonth === now.getMonth()) {
            return;
          }
          self.currentMonth--;
          if (self.currentMonth < 0) {
            self.currentMonth = 11;
            self.currentYear--;
          }
          self.render();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          self.currentMonth++;
          if (self.currentMonth > 11) {
            self.currentMonth = 0;
            self.currentYear++;
          }
          self.render();
        });
      }

      // Bind day clicks
      var dayBtns = calendarEl.querySelectorAll('.cdp-day--available, .cdp-day--soldout');
      dayBtns.forEach(function (btn) {
        if (btn.tagName === 'BUTTON') {
          btn.addEventListener('click', function () {
            self.selectDate(btn.dataset.date);
          });
        }
      });
    }

    selectDate(dateStr) {
      var variant = this.variantsByDate[dateStr];
      if (!variant) return;

      var selectedDate = this.parseDate(dateStr);
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      if (!selectedDate || selectedDate < today) return;

      this.selectedDate = dateStr;

      // Reset quantity to 1 when changing dates
      this.quantity = 1;

      // Re-render to update selected state
      this.render();

      // Update the hidden variant ID input
      var form = this.closest('form') || document.querySelector('[data-type="add-to-cart-form"]');
      if (form) {
        var idInput = form.querySelector('input[name="id"]');
        if (idInput) {
          idInput.value = variant.id;
          idInput.disabled = !variant.available;
        }
      }

      // Update the Date property
      var dateInput = document.getElementById('class-date-property-' + this.sectionId);
      if (dateInput) {
        dateInput.value = this.formatDateForProperty(dateStr);
      }

      // Sync quantity with form
      this.syncQuantity();

      // Update submit button state
      this.setSubmitState(variant.available, variant.available ? 'Book Now' : 'Sold Out');

      // Show class details summary
      this.renderDetails(variant, dateStr);

      // Update URL
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    renderDetails(variant, dateStr) {
      var infoEl = document.getElementById('class-date-info-' + this.sectionId);
      var timeslotEl = document.getElementById('class-date-timeslot-' + this.sectionId);

      if (!infoEl || !timeslotEl) return;

      infoEl.style.display = 'block';

      var startFormatted = this.formatTime(variant.startTime);
      var endFormatted = this.formatTime(variant.endTime);
      var dateFormatted = this.formatDateDisplay(dateStr);

      var html = '<div class="class-date-picker__detail">';
      html += '<div class="class-date-picker__detail-date">' + dateFormatted + '</div>';
      if (!variant.available) {
        html += '<div class="class-date-picker__detail-time"><span>' + startFormatted + ' - ' + endFormatted + '</span><span class="class-date-picker__detail-spots--soldout">Sold Out</span></div>';
        html += '<div class="class-date-picker__detail-price"><span>' + this.formatPrice(variant.price) + ' per person</span></div>';

        // Waitlist form
        html += '<div class="cdp-waitlist" data-date="' + dateStr + '">';
        html += '<div class="cdp-waitlist__heading">Join the Waitlist</div>';
        html += '<label class="cdp-waitlist__label">Name <span class="cdp-waitlist__required">*</span></label>';
        html += '<input type="text" class="cdp-waitlist__input" data-field="name" placeholder="Your name" required>';
        html += '<label class="cdp-waitlist__label">Email <span class="cdp-waitlist__required">*</span></label>';
        html += '<input type="email" class="cdp-waitlist__input" data-field="email" placeholder="you@example.com" required>';
        html += '<label class="cdp-waitlist__label">Phone <span class="cdp-waitlist__required">*</span></label>';
        html += '<input type="tel" class="cdp-waitlist__input" data-field="phone" placeholder="(555) 123-4567">';
        html += '<div class="cdp-waitlist__row">';
        html += '<label class="cdp-waitlist__label">Tickets</label>';
        html += '<select class="cdp-waitlist__select" data-field="tickets">';
        for (var t = 1; t <= 10; t++) {
          html += '<option value="' + t + '">' + t + '</option>';
        }
        html += '</select>';
        html += '</div>';
        html += '<button type="button" class="cdp-waitlist__submit">Join Waitlist</button>';
        html += '<div class="cdp-waitlist__message" style="display:none;"></div>';
        html += '</div>';
      } else {
        var spotsHtml = '';
        if (variant.inventory > 0) {
          if (variant.inventory === 1) {
            spotsHtml = 'Only 1 spot left!';
          } else if (variant.inventory <= 3) {
            spotsHtml = 'Only ' + variant.inventory + ' spots left';
          } else {
            spotsHtml = variant.inventory + ' spots left';
          }
        }
        html += '<div class="class-date-picker__detail-time"><span>' + startFormatted + ' - ' + endFormatted + '</span><span class="class-date-picker__detail-spots">' + spotsHtml + '</span></div>';

        // Price
        var unitPrice = this.formatPrice(variant.price);
        var total = this.formatPrice(variant.price * this.quantity);
        if (this.quantity > 1) {
          html += '<div class="class-date-picker__detail-price"><span>' + unitPrice + ' per person</span><strong>' + total + ' total</strong></div>';
        } else {
          html += '<div class="class-date-picker__detail-price"><span>' + unitPrice + ' per person</span></div>';
        }

        // Quantity selector
        var maxQty = variant.inventory > 0 ? variant.inventory : 99;
        html += '<div class="class-date-picker__quantity">';
        html += '<label class="class-date-picker__quantity-label">Quantity</label>';
        html += '<div class="class-date-picker__quantity-control">';
        html += '<button type="button" class="class-date-picker__qty-btn" data-action="decrease" aria-label="Decrease quantity">&minus;</button>';
        html += '<span class="class-date-picker__qty-value">' + this.quantity + '</span>';
        html += '<button type="button" class="class-date-picker__qty-btn" data-action="increase" aria-label="Increase quantity">&plus;</button>';
        html += '</div>';
        html += '</div>';

        // Occasion selector
        var currentOccasion = this.occasion || '';
        html += '<div class="class-date-picker__occasion">';
        html += '<label class="class-date-picker__occasion-label">Occasion <span class="class-date-picker__optional">(optional)</span></label>';
        html += '<select class="class-date-picker__occasion-select' + (currentOccasion === '' ? ' is-placeholder' : '') + '">';
        html += '<option value=""' + (currentOccasion === '' ? ' selected' : '') + '>Select an occasion</option>';
        html += '<option value="Birthday"' + (currentOccasion === 'Birthday' ? ' selected' : '') + '>Birthday</option>';
        html += '<option value="Anniversary"' + (currentOccasion === 'Anniversary' ? ' selected' : '') + '>Anniversary</option>';
        html += '<option value="Other"' + (currentOccasion === 'Other' ? ' selected' : '') + '>Other</option>';
        html += '</select>';
        if (currentOccasion === 'Other') {
          html += '<input type="text" class="class-date-picker__occasion-other" placeholder="Tell us about it" value="' + (this.occasionText || '') + '">';
        }
        html += '</div>';
      }

      html += '</div>';
      timeslotEl.innerHTML = html;

      // Bind quantity buttons
      var self = this;
      var qtyBtns = timeslotEl.querySelectorAll('.class-date-picker__qty-btn');
      qtyBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var action = btn.dataset.action;
          var maxQty = variant.inventory > 0 ? variant.inventory : 99;
          if (action === 'increase' && self.quantity < maxQty) {
            self.quantity++;
          } else if (action === 'decrease' && self.quantity > 1) {
            self.quantity--;
          }
          self.syncQuantity();
          self.renderDetails(variant, dateStr);
        });
      });

      // Bind occasion selector
      var occasionSelect = timeslotEl.querySelector('.class-date-picker__occasion-select');
      if (occasionSelect) {
        occasionSelect.addEventListener('change', function () {
          self.occasion = occasionSelect.value;
          self.occasionText = '';
          self.syncOccasion();
          self.renderDetails(variant, dateStr);
        });
      }

      // Bind "Other" text input
      var occasionOther = timeslotEl.querySelector('.class-date-picker__occasion-other');
      if (occasionOther) {
        occasionOther.addEventListener('input', function () {
          self.occasionText = occasionOther.value;
          self.syncOccasion();
        });
        occasionOther.focus();
      }

      // Bind waitlist submit
      var waitlistBtn = timeslotEl.querySelector('.cdp-waitlist__submit');
      if (waitlistBtn) {
        waitlistBtn.addEventListener('click', function () {
          self.submitWaitlist(dateStr);
        });
      }
    }

    submitWaitlist(dateStr) {
      var container = this.querySelector('.cdp-waitlist[data-date="' + dateStr + '"]');
      if (!container) return;

      var nameVal = (container.querySelector('[data-field="name"]').value || '').trim();
      var emailVal = (container.querySelector('[data-field="email"]').value || '').trim();
      var phoneVal = (container.querySelector('[data-field="phone"]').value || '').trim();
      var ticketsVal = container.querySelector('[data-field="tickets"]').value;
      var msgEl = container.querySelector('.cdp-waitlist__message');
      var btn = container.querySelector('.cdp-waitlist__submit');

      // Validate
      if (!nameVal || !emailVal || !phoneVal) {
        msgEl.textContent = 'Name, email, and phone are required.';
        msgEl.className = 'cdp-waitlist__message cdp-waitlist__message--error';
        msgEl.style.display = 'block';
        return;
      }

      // Basic email check
      if (emailVal.indexOf('@') === -1 || emailVal.indexOf('.') === -1) {
        msgEl.textContent = 'Please enter a valid email address.';
        msgEl.className = 'cdp-waitlist__message cdp-waitlist__message--error';
        msgEl.style.display = 'block';
        return;
      }

      // Compose body
      var dateFormatted = this.formatDateDisplay(dateStr);
      var body = 'WAITLIST REQUEST\n\n';
      body += 'Name: ' + nameVal + '\n';
      body += 'Email: ' + emailVal + '\n';
      if (phoneVal) body += 'Phone: ' + phoneVal + '\n';
      body += '\nClass: ' + this.productTitle + '\n';
      body += 'Date: ' + dateFormatted + '\n';
      body += 'Tickets requested: ' + ticketsVal + '\n';
      body += '\nThis customer would like to be notified if a spot opens up.';

      // Disable button
      btn.disabled = true;
      btn.textContent = 'Sending...';
      msgEl.style.display = 'none';

      var payload = {
        name: nameVal,
        email: emailVal,
        phone: phoneVal,
        tickets: ticketsVal,
        className: this.productTitle,
        classDate: dateFormatted
      };

      fetch('https://admin.parkerhillprovisions.com/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (response) {
        return response.json().then(function (data) {
          if (response.ok && data.success) {
            container.innerHTML = '<div class="cdp-waitlist__success">You\'re on the waitlist! We\'ll reach out if a spot opens up.</div>';
          } else {
            throw new Error(data.error || 'Unknown error');
          }
        });
      }).catch(function (err) {
        msgEl.textContent = 'Something went wrong. Please try again.';
        msgEl.className = 'cdp-waitlist__message cdp-waitlist__message--error';
        msgEl.style.display = 'block';
        btn.disabled = false;
        btn.textContent = 'Join Waitlist';
      });
    }

    syncQuantity() {
      // Update Dawn's form quantity input
      var qtyInput = document.getElementById('Quantity-' + this.sectionId);
      if (qtyInput) {
        qtyInput.value = this.quantity;
      }
    }

    syncOccasion() {
      var occasionInput = document.getElementById('class-occasion-property-' + this.sectionId);
      if (occasionInput) {
        if (this.occasion === 'Other' && this.occasionText) {
          occasionInput.value = this.occasionText;
        } else if (this.occasion === 'Other') {
          occasionInput.value = '';
        } else {
          occasionInput.value = this.occasion;
        }
      }
    }

    hideDefaultVariantPicker() {
      // Hide Dawn's default variant picker since the calendar replaces it
      var variantPicker = document.querySelector('variant-selects[data-section="' + this.sectionId + '"]');
      if (variantPicker) {
        variantPicker.style.display = 'none';
        var parentBlock = variantPicker.closest('[data-block-type="variant_picker"]') ||
                          variantPicker.closest('.product-form__input');
        if (parentBlock) {
          parentBlock.style.display = 'none';
        }
      }

      // Also try variant-radios
      var variantRadios = document.querySelector('variant-radios[data-section="' + this.sectionId + '"]');
      if (variantRadios) {
        variantRadios.style.display = 'none';
        var parentBlock = variantRadios.closest('[data-block-type="variant_picker"]') ||
                          variantRadios.closest('.product-form__input');
        if (parentBlock) {
          parentBlock.style.display = 'none';
        }
      }
    }

    hideDefaultQuantity() {
      // Hide Dawn's default quantity selector since ours is built into the detail box
      var qtyForm = document.getElementById('Quantity-Form-' + this.sectionId);
      if (qtyForm) {
        qtyForm.style.display = 'none';
      }
    }
  }

  customElements.define('class-date-picker', ClassDatePicker);
}
